import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const FRAME_MAX_HEIGHT = 760;

const MAIL_FRAME_STYLE = [
  ":root{color-scheme:light}",
  "html,body{margin:0;padding:0}",
  'body{font-family:Roboto,"Helvetica Neue",Arial,"PingFang SC","Microsoft YaHei",sans-serif;',
  "font-size:14px;line-height:1.6;color:#202124;background:#fff;",
  "overflow-wrap:anywhere;word-break:break-word}",
  "img{max-width:100%;height:auto}",
  "table{width:auto;max-width:100%;border-collapse:collapse}",
  "th,td{min-width:72px;padding:7px 9px;border:1px solid #cfd8e3;",
  "vertical-align:top;text-align:left}",
  "th{background:#f5f7fa;font-weight:600}",
  "blockquote{margin:0 0 0 1px;padding-left:12px;border-left:2px solid #dadce0;color:#5f6368}",
  "a{color:#1a73e8}",
  "span[data-mention-user-id]{color:#175cd3;background:#eff6ff;border-radius:4px;padding:1px 4px;font-weight:600}",
  "pre{white-space:pre-wrap}",
  "*{max-width:100%}",
].join("");
const QUOTED_HISTORY_STYLE = [
  ".gmail_quote,.crm-quoted-history{margin-top:22px;padding:0;",
  "border-top:0;background:transparent;color:#6b7280}",
  "blockquote{margin-top:14px;padding:0 0 0 14px;border-top:0;",
  "border-left:2px solid #d1d5db;background:transparent;color:#6b7280}",
  ".gmail_quote blockquote{margin:14px 0 0;padding:0 0 0 14px;",
  "border-top:0;background:transparent}",
  ".gmail_attr{display:block;margin-top:22px;color:#6b7280}",
  ".gmail_quote>.gmail_attr{margin-top:0}",
].join("");

/**
 * Sandboxed document for the rich-text body of an original message.
 *
 * The body is the customer's own HTML, so it is rendered in a frame that runs no scripts, loads no
 * remote resources and cannot navigate the workbench. `default-src 'none'` keeps that true even if
 * a stored body still carries an image or link the ingest sanitizer did not see.
 */
export function mailFrameDocument(
  html: string,
  compact = false,
  emphasizeQuotedHistory = false,
  transparentBackground = false,
): string {
  const policy = [
    "default-src 'none'",
    "img-src 'self' data:",
    "style-src 'unsafe-inline'",
    "form-action 'none'",
    "base-uri 'none'",
  ].join("; ");
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${policy}">`,
    `<style>${MAIL_FRAME_STYLE}${compact ? "body{font-size:12px}" : ""}${emphasizeQuotedHistory ? QUOTED_HISTORY_STYLE : ""}${transparentBackground ? "html,body{background:transparent}" : ""}</style>`,
    "</head><body>",
    html,
    "</body></html>",
  ].join("");
}

export interface MailParagraph {
  readonly kind: "BODY" | "QUOTE";
  readonly lines: readonly string[];
}

const QUOTE_MARKERS = [
  /^On\s.+wrote:\s*$/i,
  /^在\s?.+写道[:：]\s*$/,
  /^-{2,}\s*(?:Original Message|原始邮件|转发邮件|Forwarded message)\s*-{2,}$/i,
];

/**
 * Splits the readable body into paragraphs and marks quoted history.
 *
 * Gmail keeps a reply readable by folding the quoted conversation behind a toggle; the same shape
 * is used here once a reply marker shows up with real content behind it.
 */
export function toMailParagraphs(text: string): MailParagraph[] {
  const paragraphs: MailParagraph[] = [];
  let current: string[] = [];
  const flush = () => {
    if (!current.length) return;
    const quoted = current.every((line) => /^>/.test(line.trim()));
    paragraphs.push({ kind: quoted ? "QUOTE" : "BODY", lines: current });
    current = [];
  };
  for (const line of text.replace(/\r\n?/g, "\n").split("\n")) {
    if (!line.trim()) {
      flush();
      continue;
    }
    current.push(line);
  }
  flush();
  return paragraphs;
}

export function collapsesQuotedHistory(
  paragraphs: readonly MailParagraph[],
): boolean {
  // The paragraph that introduces the quoted conversation is folded together with everything below
  // it. A body that never leaves the new reply above the toggle is shown in full.
  return paragraphs.some(
    (paragraph, index) =>
      index >= 1 &&
      (paragraph.kind === "QUOTE" ||
        QUOTE_MARKERS.some((marker) =>
          marker.test(paragraph.lines[0]?.trim() ?? ""),
        )),
  );
}

const LINK_PATTERN =
  /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g;
const TRAILING_PUNCTUATION = /[.,;:!?，。；：！？）)]+$/;

export function renderLinkedLine(line: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  for (const match of line.matchAll(LINK_PATTERN)) {
    const start = match.index ?? 0;
    const candidate = match[0];
    const trailing = TRAILING_PUNCTUATION.exec(candidate)?.[0] ?? "";
    const value = candidate.slice(0, candidate.length - trailing.length);
    if (!value) continue;
    if (start > cursor) nodes.push(line.slice(cursor, start));
    const href = value.includes("@")
      ? `mailto:${value}`
      : value.startsWith("www.")
        ? `https://${value}`
        : value;
    nodes.push(
      <a
        key={`${keyPrefix}-link-${start}`}
        href={href}
        target="_blank"
        rel="noreferrer noopener"
      >
        {value}
      </a>,
    );
    if (trailing) nodes.push(trailing);
    cursor = start + candidate.length;
  }
  if (cursor < line.length) nodes.push(line.slice(cursor));
  return nodes;
}

function Paragraph({
  paragraph,
  keyPrefix,
}: {
  paragraph: MailParagraph;
  keyPrefix: string;
}) {
  return (
    <p
      className={
        paragraph.kind === "QUOTE" ? "triage-mail-plain-quote" : undefined
      }
    >
      {paragraph.lines.map((line, index) => (
        <span key={`${keyPrefix}-line-${index}`}>
          {index > 0 ? <br /> : null}
          {renderLinkedLine(line, `${keyPrefix}-${index}`)}
        </span>
      ))}
    </p>
  );
}

/** Gmail-like plain-text rendering of a stored message body. */
export function MailPlainText({
  text,
  emptyLabel,
}: {
  text: string | null | undefined;
  emptyLabel: string;
}) {
  const paragraphs = useMemo(() => toMailParagraphs(text ?? ""), [text]);
  if (!paragraphs.length) {
    return <p className="triage-mail-plain is-empty">{emptyLabel}</p>;
  }
  const fold = collapsesQuotedHistory(paragraphs);
  const head = fold ? paragraphs.slice(0, 1) : paragraphs;
  const tail = fold ? paragraphs.slice(1) : [];
  return (
    <div className="triage-mail-plain">
      {head.map((paragraph, index) => (
        <Paragraph
          key={`head-${index}`}
          paragraph={paragraph}
          keyPrefix={`head-${index}`}
        />
      ))}
      {fold ? (
        <details className="triage-mail-plain-history">
          <summary aria-label="显示引用内容">···</summary>
          {tail.map((paragraph, index) => (
            <Paragraph
              key={`tail-${index}`}
              paragraph={paragraph}
              keyPrefix={`tail-${index}`}
            />
          ))}
        </details>
      ) : null}
    </div>
  );
}

function RichMailFrame({ html }: { html: string }) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const measure = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    try {
      const document_ = frame.contentDocument;
      const body = document_?.body;
      if (!document_ || !body) return;
      const next = Math.ceil(
        Math.max(
          body.scrollHeight,
          document_.documentElement?.scrollHeight ?? 0,
        ),
      );
      setHeight(next > 0 ? Math.min(next + 4, FRAME_MAX_HEIGHT) : null);
    } catch {
      setHeight(null);
    }
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(measure, 80);
    return () => window.clearTimeout(timer);
  }, [measure, html]);
  return (
    <iframe
      ref={frameRef}
      className="triage-mail-frame"
      title="邮件正文（富文本）"
      sandbox="allow-same-origin"
      referrerPolicy="no-referrer"
      srcDoc={mailFrameDocument(html)}
      style={height ? { height: `${height}px` } : undefined}
      onLoad={measure}
    />
  );
}

/**
 * Original message body for the mail view: the message's own HTML when it has one, with the safe
 * plain text kept one click away.
 */
export function OriginalMailBody({
  textBody,
  htmlBody,
  emptyLabel = "正文为空，无法从邮件正文补充字段。",
}: {
  textBody: string | null | undefined;
  htmlBody?: string | null;
  emptyLabel?: string;
}) {
  const hasHtml = Boolean(htmlBody?.trim());
  const [chosen, setChosen] = useState<"rich" | "plain" | null>(null);
  const view = chosen ?? (hasHtml ? "rich" : "plain");
  const hint =
    view === "rich"
      ? "沙箱渲染 · 不执行脚本，不加载外部图片"
      : hasHtml
        ? "纯文本视图 · 保留完整正文"
        : "安全纯文本视图 · 保留完整正文";
  return (
    <article className="triage-original-body">
      <header>
        <strong>正文</strong>
        <div className="triage-original-body-actions">
          {hasHtml ? (
            <div
              className="triage-mail-view-switch"
              role="group"
              aria-label="正文视图"
            >
              <button
                type="button"
                aria-pressed={view === "rich"}
                onClick={() => setChosen("rich")}
              >
                富文本
              </button>
              <button
                type="button"
                aria-pressed={view === "plain"}
                onClick={() => setChosen("plain")}
              >
                纯文本
              </button>
            </div>
          ) : null}
          <span>{hint}</span>
        </div>
      </header>
      {view === "rich" && hasHtml ? (
        <RichMailFrame html={htmlBody ?? ""} />
      ) : (
        <MailPlainText text={textBody} emptyLabel={emptyLabel} />
      )}
    </article>
  );
}
